/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useCallback } from "react";
import MainLayout from "../../../layouts/MainLayout";
import Form from "../../../components/forms/Form";
import PageHeader from "../../../components/PageHeader";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import "../../../assets/styles/custom-styles.css";
import { editCustomerFields } from "../../../constants/forms/customerFields"; // Assumindo que este arquivo existe e está correto
import useLoader from "../../../hooks/useLoader";
import useForm from "../../../hooks/useForm";
import { maskCpfCnpj, removeMask, removeMaskCpfCnpj } from "../../../utils/maskUtils";
import {
  cleanPayload,
  normalizeFormErrors,
  setDefaultFieldValues,
} from "../../../utils/objectUtils";
import useBaseService from "../../../hooks/services/useBaseService";
import { entities } from "../../../constants/entities";
import SimpleBody from "../../../components/forms/SimpleBody";
import SectionHeader from "../../../components/forms/SectionHeader";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import useNotification from "../../../hooks/useNotification";
import useRemoteSelectOptions from "../../../hooks/useRemoteSelectOptions";
import { transformFiltersForUrl } from "../../../utils/transformFiltersForUrl";
import useQueryWithLoader from "../../../hooks/useQueryWithLoader";

const EditCustomerPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const { showNotification } = useNotification();
  const { showLoader, hideLoader } = useLoader();
  const queryClient = useQueryClient();

  const {
    getByColumn: baseServiceGetByColumn,
    get: baseServiceGet,
    put: baseServicePut,
    formErrors,
    setFormErrors,
  } = useBaseService(navigate);
  const { formData, setFormData, handleChange } = useForm(
    setDefaultFieldValues(editCustomerFields),
    editCustomerFields,
  );

  // --- Query para buscar os dados do cliente ---
  const { data: customerData, isLoading: isLoadingCustomer } =
    useQueryWithLoader({
      queryKey: ["customer", id],
      queryFn: async () => {
        const response = await baseServiceGetByColumn(
          entities.customers.getById(id),
        );
        return response.result;
      },
      enabled: !!id,
      staleTime: 5 * 60 * 1000,
    });

  const parentSelect = useRemoteSelectOptions({
    queryKey: ["customers"],
    fetcher: async ({ page = 1, perPage = 15, search = "" }) => {
      const filters = transformFiltersForUrl({
        deleted_at: false,
        alias: { $nin: ["ALUCOM", "MOREIA", "IP", "AS SISTEMAS"] },
        ...(search && { alias: search }),
      });
      const params = { filters, page, perPage };

      const response = await baseServiceGet(entities.customers.get, params);
      return response.result;
    },
    mapToOption: (item) => ({
      value: item.id,
      label: `${item.id} - ${item.alias}`,
    }),
    fieldId: "customer.parent_id",
    immediateFetch: true,
    formData,
    setFormData,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
  });

  const selects = {
    "customer.parent_id": parentSelect,
  };

  const getSelectMeta = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId];
      if (!selectConfig) return {};

      return {
        hasNextPage: selectConfig.hasNextPage,
        isFetchingNextPage: selectConfig.isFetchingNextPage,
        isLoading: selectConfig.isLoading,
        isFetching: selectConfig.isFetching,
        handleMenuScrollToBottom: selectConfig.handleMenuScrollToBottom,
        handleSearch: selectConfig.handleSearch,
        onFocus: selectConfig.activateQuery,
      };
    },
    [selects],
  );

  // --- Funções para Selects (getOptions e getSelectedValue) ---
  const getOptions = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId];
      return selectConfig?.options || [];
    },
    [selects],
  );

  const getSelectedValue = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId];
      return selectConfig?.selectedValue || null;
    },
    [selects],
  );

  useEffect(() => {
    if (customerData) {
      setFormData({
        customer: customerData,
      });
    }
  }, [customerData, setFormData]);

  // --- Mutation para atualizar os dados do cliente ---
  const updateCustomerMutation = useMutation({
    mutationFn: async (dataToUpdate) => {
      showLoader();
      setFormErrors({});
      const response = await baseServicePut(
        entities.customers.update(id),
        dataToUpdate,
      );
      return response;
    },
    onSuccess: (data) => {
      if (data?.status === 200) {
        queryClient.invalidateQueries({ queryKey: ["customer", id] });
        queryClient.invalidateQueries({ queryKey: ["customers"] });
        navigate("/aluguel/clientes", { state: { type: "success" } });
      }
    },
    onError: (err) => {
      console.error("onError ", err);
      if (err.response?.data?.errors) {
        setFormErrors(err.response.data.errors);
      }
    },
    onSettled: () => {
      hideLoader();
    },
  });

  const handleSubmit = useCallback(async () => {
    try {
      const cleanedData = cleanPayload(formData);
      const payload = {
        customer: {
          ...cleanedData.customer,
          cpf_cnpj: String(removeMaskCpfCnpj(formData.customer.cpf_cnpj)),
        },
      };
      await updateCustomerMutation.mutateAsync(payload);
    } catch (error) {
      console.error("Erro ao enviar cliente:", {
        status: error.status,
        message: error.message,
      });
    }
  }, [formData, updateCustomerMutation]);

  // --- Efeito para exibir notificações da rota (se houver) ---
  useEffect(() => {
    if (location.state?.message) {
      showNotification(location.state.type, location.state.message);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, showNotification, navigate, location.pathname]);

  const isPageLoading = isLoadingCustomer;
  const isSubmitting = updateCustomerMutation.isPending;

  const renderForm = useCallback(() => {
    const renderedSections = new Set();

    return editCustomerFields.map((section, index) => {
      const sectionType = section.section.split(" - ")[0];
      const showHeader = !renderedSections.has(sectionType);

      if (showHeader) {
        renderedSections.add(sectionType);
      }

      return (
        <div key={section.section + " " + index}>
          {showHeader && (
            <SectionHeader
              section={section.section}
              viewTable={false}
              setViewTable={() => {}}
              addFieldsInData={() => {}}
            />
          )}

          <SimpleBody
            fields={section.fields}
            formErrors={normalizeFormErrors(formErrors)}
            formData={formData}
            handleFieldChange={(fieldId, value) => {
              // fieldId aqui é o nome do campo (ex: 'name', 'cpf_cnpj')
              if (fieldId === "cpf_cnpj") {
                handleChange(fieldId, maskCpfCnpj(value));
              } else {
                handleChange(fieldId, value);
              }
            }}
            getOptions={getOptions}
            getSelectedValue={getSelectedValue}
            getSelectMeta={getSelectMeta}
          />
        </div>
      );
    });
  }, [formData, formErrors, handleChange, getOptions, getSelectedValue]);

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title="Editar Cliente"
        showBackButton={true}
        backUrl="/aluguel/clientes/"
      />
      <div className="container-fluid p-1">
        <Form
          onSubmit={handleSubmit}
          textSubmit="Atualizar"
          initialFormData={formData}
          textLoadingSubmit="Atualizando..."
          isLoading={isPageLoading || isSubmitting}>
          {renderForm}
        </Form>
      </div>
    </MainLayout>
  );
};

export default EditCustomerPage;
